import { useState, useEffect, useCallback, useRef } from "react";
import { useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { Draggable } from "react-beautiful-dnd";
import { Box, Button, Grid, Typography, Chip, Alert } from "@mui/material";
import SaveIcon from "@mui/icons-material/Save";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorIcon from "@mui/icons-material/Error";
import { transformCitationLanguage } from "../../../utilities/helpers";
import SimpleAccordin from "../../../components/SimpleAccordin";
import _ from "lodash";
import EqipRichTextEditor from "../../../components/EqipRichTextEditor";
import ieqipCoreServiceApi from "../../../apis/ieqipCoreServiceApi";
import styled from "@emotion/styled";
import { setInspection } from "../../../state";

// The read-only role check is an inspection-level fact (does the logged-in user
// hold role 99 for this inspection), identical for every citation row. CoverageDraggable
// renders once per row, so without sharing each row fired its own /assignmentassigntos
// request — dozens of identical GETs serialized on one connection. Cache the request per
// inspection so every row reuses a single fetch.
const assignTosRequestCache = new Map();

const getAssignTos = (inspectionUid) => {
  if (!assignTosRequestCache.has(inspectionUid)) {
    const request = ieqipCoreServiceApi
      .get(`api/assignmentassigntos/inspectionuid/${inspectionUid}`)
      .then((response) => response.data)
      .catch((error) => {
        // Drop the failed promise so a later mount can retry.
        assignTosRequestCache.delete(inspectionUid);
        throw error;
      });
    assignTosRequestCache.set(inspectionUid, request);
  }
  return assignTosRequestCache.get(inspectionUid);
};

const CoverageDraggable = ({ questionCitation, index, type }) => {
  // All hooks must be called at the top level
  const dispatch = useDispatch();
  const { inspectionUid } = useParams();
  const loggedInUser = useSelector((state) => {
    return state.user;
  });
  
  // Initialize with safe defaults
  const citationLanguage = questionCitation?.detail?.citationLanguage || '';
  const citationSpecifically = questionCitation?.detail?.citationSpecifically || '';
  
  const [value, setValue] = useState(citationSpecifically);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [saveStatus, setSaveStatus] = useState(null); // 'success', 'error', null
  const [lastSaveTime, setLastSaveTime] = useState(null);
  const [isReadOnlyUser, setIsReadOnlyUser] = useState(false);
  const [userRoleName, setUserRoleName] = useState('');
  
  // Refs for timeout cleanup
  const successTimeoutRef = useRef(null);
  const errorTimeoutRef = useRef(null);
  const debouncedSaveRef = useRef(null);
  
  const transformedCitationLanguage = transformCitationLanguage(citationLanguage);
  
  // Check if current user has read-only role (role_id 99)
  const checkUserRole = useCallback(async () => {
    try {
      const assignTos = await getAssignTos(inspectionUid);
      const currentUserAssignment = assignTos.find(assignment =>
        assignment.assign_to === loggedInUser.email
      );
      
      if (currentUserAssignment?.role_id === 99) {
        setIsReadOnlyUser(true);
        setUserRoleName('Read-Only Role');
        // Try to fetch role name
        // try {
        //   const roleResponse = await ieqipCoreServiceApi.get(`api/roles/${currentUserAssignment.role_id}`);
        //   setUserRoleName(roleResponse.data?.name || roleResponse.data?.role_name || 'Read-Only Role');
        // } catch (roleError) {
        //   console.error("Failed to fetch role name:", roleError);
        //   setUserRoleName('Read-Only Role'); // Fallback name
        // }
      } else {
        setIsReadOnlyUser(false);
        setUserRoleName('');
      }
    } catch (error) {
      console.error("Failed to check user role:", error);
      // Default to allowing edits if we can't determine role
      setIsReadOnlyUser(false);
      setUserRoleName('');
    }
  }, [inspectionUid, loggedInUser.email]);

  // Clear timeouts helper
  const clearTimeouts = useCallback(() => {
    if (successTimeoutRef.current) {
      clearTimeout(successTimeoutRef.current);
      successTimeoutRef.current = null;
    }
    if (errorTimeoutRef.current) {
      clearTimeout(errorTimeoutRef.current);
      errorTimeoutRef.current = null;
    }
  }, []);

  const updateInspection = useCallback(async (showManualLoading = true) => {
    if (isReadOnlyUser) {
      setSaveStatus('error');
      errorTimeoutRef.current = setTimeout(() => {
        setSaveStatus(null);
      }, 5000);
      return;
    }

    if (showManualLoading) {
      setIsLoading(true);
    }
    setIsSaving(true);
    setSaveStatus(null);
    clearTimeouts();
    
    try {
      if (questionCitation.lookup_code_type === '503A') {
        const originalOperation = questionCitation.originalOperation || {};
        const updatedOperation = {
          ...originalOperation,
          detail: {
            ...(originalOperation.detail || {}),
            citationSpecifically: value,
          },
        };
        await ieqipCoreServiceApi.post(`/api/inspections/inspection503aoperations`, updatedOperation);
      } else if (questionCitation.lookup_code_type === '503B') {
        const originalOperation = questionCitation.originalOperation || {};
        const updatedOperation = {
          ...originalOperation,
          detail: {
            ...(originalOperation.detail || {}),
            citationSpecifically: value,
          },
        };
        const uid = originalOperation.inspection_503b_operation_uid;
        await ieqipCoreServiceApi.put(`/api/inspections/inspection503boperations/${uid}`, updatedOperation);
      } else {
        await ieqipCoreServiceApi.patch(
          `/api/inspections/inspectiondetails/${questionCitation.inspection_detail_uid}/detail`,
          { citationSpecifically: value }
        );
      }
      
      setSaveStatus('success');
      setLastSaveTime(new Date());
      
      // Clear success message after 3 seconds
      successTimeoutRef.current = setTimeout(() => {
        setSaveStatus(null);
      }, 3000);
      
    } catch (error) {
      console.error('Save error:', error);
      setSaveStatus('error');
      
      // Clear error message after 5 seconds
      errorTimeoutRef.current = setTimeout(() => {
        setSaveStatus(null);
      }, 5000);
    } finally {
      setIsLoading(false);
      setIsSaving(false);
    }
  }, [value, questionCitation?.lookup_code_type, questionCitation?.originalOperation, questionCitation?.inspection_detail_uid, clearTimeouts, isReadOnlyUser]);

  const handleValueChange = useCallback((newValue) => {
    if (isReadOnlyUser) return;
    setValue(newValue);
    setSaveStatus(null); // Clear any previous status when user starts typing
  }, [isReadOnlyUser]);

  const handleManualSave = useCallback(() => {
    if (isReadOnlyUser) return;
    updateInspection(true);
  }, [updateInspection, isReadOnlyUser]);

  // Debounced auto-save function
  useEffect(() => {
    // Cancel previous debounced call
    if (debouncedSaveRef.current) {
      debouncedSaveRef.current.cancel();
    }
    
    // Create new debounced function
    debouncedSaveRef.current = _.debounce(() => {
      updateInspection(false);
    }, 2000);

    return () => {
      if (debouncedSaveRef.current) {
        debouncedSaveRef.current.cancel();
      }
    };
  }, [updateInspection]);

  // Auto-save effect
  useEffect(() => {
    if (value !== citationSpecifically && value.trim() !== '' && debouncedSaveRef.current && !isReadOnlyUser) {
      debouncedSaveRef.current();
    }
  }, [value, citationSpecifically, isReadOnlyUser]);

  // Cleanup timeouts on unmount
  useEffect(() => {
    return () => {
      clearTimeouts();
      if (debouncedSaveRef.current) {
        debouncedSaveRef.current.cancel();
      }
    };
  }, [clearTimeouts]);

  // Check user role on component mount
  useEffect(() => {
    checkUserRole();
  }, [checkUserRole]);

  // Early return after all hooks are called
  if (!questionCitation || !questionCitation.detail) {
    return null;
  }

  // Click handler for reference navigation
  const handleReferenceClick = () => {
    // Extract dynamic values from questionCitation
    const citationLanguageCode = questionCitation.code;
    const subPartCode = questionCitation.sub_part_code || 'SP002'; // fallback to SP002 if not available
    const lookupCodeType = questionCitation.lookup_code_type || 'CFR211'; // fallback to CFR211 if not available
    
    const url = `/inspection/${inspectionUid}/subPartCode/${subPartCode}/citationLanguageCode/${citationLanguageCode}/lookupcodetype/${lookupCodeType}`;
    
    dispatch(
      setInspection({
        isShowInspection: true
      })
    );
    // Force a full page reload to ensure sidebar reloads and expands correct submenu
    window.location.href = url;
  };

  const ReferenceGrid = styled(Grid)`
    padding: 12px;
    background-color: #f5f5f5;
    border-radius: 4px;
    margin: 8px 0;
  `;

  const StatusContainer = styled(Box)`
    display: flex;
    align-items: center;
    gap: 8px;
    margin: 8px 0;
    min-height: 32px;
  `;

  const renderSaveStatus = () => {
    if (isSaving) {
      return (
        <Chip
          icon={<SaveIcon />}
          label="Saving..."
          size="small"
          color="primary"
          variant="outlined"
        />
      );
    }

    if (saveStatus === 'success') {
      return (
        <Chip
          icon={<CheckCircleIcon />}
          label={`Saved${lastSaveTime ? ` at ${lastSaveTime.toLocaleTimeString()}` : ''}`}
          size="small"
          color="success"
          variant="outlined"
        />
      );
    }

    if (saveStatus === 'error') {
      return (
        <Chip
          icon={<ErrorIcon />}
          label={isReadOnlyUser ? "Cannot save - read-only mode" : "Save failed - please try again"}
          size="small"
          color="error"
          variant="outlined"
        />
      );
    }

    return null;
  };

  return (
    <Draggable draggableId={questionCitation.code} index={index}>
      {(provided) => (
        <Box m="2px" {...provided.draggableProps} {...provided.dragHandleProps} ref={provided.innerRef}>
          <SimpleAccordin title={`${_.camelCase(type)} ${index + 1}. ${transformedCitationLanguage}`} isDragNDrop>
            <Grid container spacing={1}>
              {isReadOnlyUser && (
                <Grid item xs={12}>
                  <Alert 
                    severity="info"
                    sx={{ marginBottom: '0.5em' }}
                  >
                    {`You are in read-only mode. You cannot edit this inspection because you have "${userRoleName}".`}
                  </Alert>
                </Grid>
              )}
              
              <ReferenceGrid item xs={12}>
                Reference: <Typography 
                  component="span" 
                  onClick={handleReferenceClick}
                  sx={{ 
                    color: 'primary.main', 
                    cursor: 'pointer', 
                    textDecoration: 'underline',
                    '&:hover': {
                      color: 'primary.dark'
                    }
                  }}
                >
                  {questionCitation.reference_description}
                </Typography>
              </ReferenceGrid>
              
              <StatusContainer>
                {renderSaveStatus()}
                {!isSaving && !saveStatus && !isReadOnlyUser && (
                  <Typography variant="caption" color="text.secondary">
                    Auto-save enabled
                  </Typography>
                )}
              </StatusContainer>
              
              <Grid item xs={12}>
                <EqipRichTextEditor 
                  fullWidth 
                  theme="snow" 
                  value={value} 
                  onChange={handleValueChange}
                  placeholder="Specifically"
                  readOnly={isReadOnlyUser}
                />
              </Grid>
              
              {!isReadOnlyUser && (
                <Grid item xs={12}>
                  <Button 
                    type="submit" 
                    color="primary" 
                    variant="contained" 
                    onClick={handleManualSave} 
                    disabled={isLoading || isSaving} 
                    startIcon={<SaveIcon />}
                  >
                    {isLoading ? 'Saving...' : 'Save Now'}
                  </Button>
                </Grid>
              )}
            </Grid>
          </SimpleAccordin>
        </Box>
      )}
    </Draggable>
  );
};

export default CoverageDraggable; 